import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../config/authConfig';
import { msalInstance } from '../config/msalInstance';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Route the user lands on when the session cannot be recovered (see App.tsx routes).
const LOGIN_PATH = '/auth/login';

const AUTH_HEADER = 'Authorization';

/**
 * Create an Axios instance for making API requests
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Flags this module attaches to a request config.
 * `_retried`         - this request has already consumed its single refresh-and-retry.
 * `_skipAuthRefresh` - opt a request out of the refresh flow entirely.
 */
type AuthAwareConfig = InternalAxiosRequestConfig & {
  _retried?: boolean;
  _skipAuthRefresh?: boolean;
};

/**
 * Add token to authorization header for authenticated requests
 * @param token Authentication token
 */
export const setAuthToken = (token: string): void => {
  apiClient.defaults.headers.common[AUTH_HEADER] = `Bearer ${token}`;
};

/**
 * Remove token from authorization header
 */
export const clearAuthToken = (): void => {
  delete apiClient.defaults.headers.common[AUTH_HEADER];
};

/**
 * Make a GET request to the API
 * @param url Endpoint URL
 * @param config Additional Axios request configuration
 * @returns Promise with response data
 */
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.get<T>(url, config);
  return response.data;
};

/**
 * Make a GET request to the API and return the full response object
 * @param url Endpoint URL
 * @param config Additional Axios request configuration
 * @returns Promise with full Axios response
 */
export const getRaw = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiClient.get<T>(url, config);
    console.log('Raw GET response:', response.data);
    return response.data; // Return just the data, which is the raw T type
};

/**
 * Make a POST request to the API
 * @param url Endpoint URL
 * @param data Request body data
 * @param config Additional Axios request configuration
 * @returns Promise with response data
 */
export const post = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
};

/**
 * Make a PUT request to the API
 * @param url Endpoint URL
 * @param data Request body data
 * @param config Additional Axios request configuration
 * @returns Promise with response data
 */
export const put = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
};

/**
 * Make a PATCH request to the API
 * @param url Endpoint URL
 * @param data Request body data
 * @param config Additional Axios request configuration
 * @returns Promise with response data
 */
export const patch = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
};

/**
 * Make a DELETE request to the API
 * @param url Endpoint URL
 * @param config Additional Axios request configuration
 * @returns Promise with response data
 */
export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
};

// ---------------------------------------------------------------------------
// Session refresh (401 handling)
//
// The Entra access token used to be written into the default headers exactly once,
// at sign-in. Entra access tokens live ~60-90 minutes, so every protected call in a
// tab left open past that point returned 401 until the user reloaded the page. The
// interceptor below acquires a fresh token and replays the failed request once.
// ---------------------------------------------------------------------------

/** Absolute root of our own API - used to decide whether a 401 is ours to act on. */
const apiRoot: URL | null = (() => {
  try {
    return new URL(API_BASE_URL, window.location.origin);
  } catch {
    return null;
  }
})();

/** Resolve a request config to an absolute URL the same way Axios builds the full path. */
const resolveRequestUrl = (config?: AxiosRequestConfig): URL | null => {
  if (!config) {
    return null;
  }
  const requestUrl = config.url ?? '';
  try {
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(requestUrl)) {
      // Absolute URL on the request itself - Axios ignores baseURL in that case.
      return new URL(requestUrl);
    }
    const base = String(config.baseURL ?? API_BASE_URL).replace(/\/+$/, '');
    return new URL(`${base}/${requestUrl.replace(/^\/+/, '')}`, window.location.origin);
  } catch {
    return null;
  }
};

/**
 * True only for requests aimed at our own API.
 *
 * A 401 relayed from some downstream system (or from a third-party host a caller
 * pointed at) is not our session expiring; refreshing on it would hide a real
 * authorization failure behind a token round-trip.
 */
const isOurApi = (config?: AxiosRequestConfig): boolean => {
  const url = resolveRequestUrl(config);
  if (!url || !apiRoot || url.origin !== apiRoot.origin) {
    return false;
  }
  const root = apiRoot.pathname.replace(/\/+$/, '');
  return url.pathname === root || url.pathname.startsWith(`${root}/`);
};

/**
 * Interactive sign-in is only ever acceptable while the user is actually looking at
 * this tab. A popup raised by a background poll is a UX trap and a clickjacking
 * surface, so a hidden or unfocused tab fails closed instead.
 */
const canPromptInteractively = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }
  return (
    document.visibilityState === 'visible' &&
    typeof document.hasFocus === 'function' &&
    document.hasFocus()
  );
};

/** Silent first; interactive only when MSAL says interaction is genuinely required. */
const acquireFreshToken = async (): Promise<string> => {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account) {
    throw new Error('No signed-in account available to refresh the session.');
  }

  try {
    const silent = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    return silent.accessToken;
  } catch (silentError) {
    if (!(silentError instanceof InteractionRequiredAuthError)) {
      throw silentError;
    }
    if (!canPromptInteractively()) {
      throw new Error(
        'Interaction required to refresh the session, but this tab is not in the foreground.'
      );
    }
    const interactive = await msalInstance.acquireTokenPopup({
      ...apiRequest,
      account,
      redirectUri: `${window.location.origin}/blank.html`,
    });
    return interactive.accessToken;
  }
};

/**
 * Single-flight token acquisition.
 *
 * Ten simultaneous 401s must produce exactly one token request, not ten. Callers
 * after the first await the in-flight promise; the slot is released in `finally`
 * so a later expiry can start a fresh acquisition.
 */
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }
  const pending: Promise<string> = acquireFreshToken().finally(() => {
    if (refreshPromise === pending) {
      refreshPromise = null;
    }
  });
  refreshPromise = pending;
  return pending;
};

/** Set once so a burst of failures produces a single navigation, not one per request. */
let sessionExpiryHandled = false;

/**
 * Fail closed: drop the stale credential and hand the user a clean sign-in.
 * The hard navigation also discards the in-memory Redux auth state, so the app
 * cannot keep rendering as half-authenticated.
 */
const handleSessionExpired = (): void => {
  clearAuthToken();

  if (sessionExpiryHandled || typeof window === 'undefined') {
    return;
  }
  sessionExpiryHandled = true;

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
};

/** Write the refreshed token onto the request being replayed. */
const applyAuthHeader = (config: AuthAwareConfig, token: string): void => {
  const value = `Bearer ${token}`;
  const headers = config.headers as unknown as
    | { set?: (name: string, value: string) => void }
    | undefined;

  if (headers && typeof headers.set === 'function') {
    headers.set(AUTH_HEADER, value);
    return;
  }
  config.headers = { ...(config.headers as any), [AUTH_HEADER]: value } as any;
};

/**
 * 401 refresh-and-retry.
 *
 * Registered BEFORE the error-shaping interceptor below so it still sees the raw
 * AxiosError (with `config`); anything it rejects falls through to that one and
 * reaches callers in the existing `{ status, message, data }` shape.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as AuthAwareConfig | undefined;
    const status = error.response?.status;

    if (
      status !== 401 ||      // 403 is "authenticated but not permitted" - retrying masks it
      !config ||
      config._retried ||     // one retry per request, never a loop
      config._skipAuthRefresh ||
      !isOurApi(config)
    ) {
      return Promise.reject(error);
    }

    // Claim the single retry before awaiting anything, so a replay that 401s again
    // cannot start a second round.
    config._retried = true;

    const requestUrl = resolveRequestUrl(config);
    const responseHeaders = (error.response?.headers ?? {}) as Record<string, unknown>;
    const correlationId =
      responseHeaders['x-correlation-id'] ?? responseHeaders['x-request-id'] ?? null;

    // Never log the token, any fragment of it, or the Authorization header.
    console.warn('Session token rejected (401); refreshing.', {
      method: (config.method || 'get').toUpperCase(),
      path: requestUrl ? requestUrl.pathname : null,
      status,
      correlationId,
    });

    let token: string;
    try {
      token = await refreshAccessToken();
    } catch (refreshError) {
      // Error class only - never the message body, which can echo request material.
      console.error('Session refresh failed; signing out.', {
        path: requestUrl ? requestUrl.pathname : null,
        correlationId,
        reason: refreshError instanceof Error ? refreshError.name : 'unknown',
      });
      handleSessionExpired();
      return Promise.reject(error);
    }

    setAuthToken(token);            // every future request
    applyAuthHeader(config, token); // and the one being replayed right now

    return apiClient.request(config);
  }
);

// Error interceptor to handle API errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle error responses
    const errorResponse = {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    };

    console.error('API Error:', errorResponse);

    // Re-throw the error for the caller to handle
    return Promise.reject(errorResponse);
  }
);

export default apiClient;